import { useState } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import BookingModal from '../home/components/BookingModal';

interface FAQItem {
  question: string;
  answer: string;
}

interface FAQSection {
  title: string;
  items: FAQItem[];
}

export default function FAQPage() {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});

  const openBookingModal = () => setIsBookingModalOpen(true);
  const closeBookingModal = () => setIsBookingModalOpen(false);

  const toggleItem = (sectionIndex: number, itemIndex: number) => {
    const key = `${sectionIndex}-${itemIndex}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const faqSections: FAQSection[] = [
    {
      title: 'Grundverständnis KI & KI‑Assistent:innen',
      items: [
        {
          question: 'Was ist ein KI‑Assistent überhaupt?',
          answer: 'Ein KI‑Assistent ist eine digitale Unterstützung, die wiederkehrende Aufgaben übernimmt: Informationen suchen, Texte entwerfen, Daten zusammenfassen, Fragen beantworten oder Prozesse anstoßen. Statt „Zauberkiste" denken wir bei klar:ki an zusätzliche Kolleg:innen mit klaren Rollen und Grenzen – immer verfügbar, aber von Menschen geführt.'
        },
        {
          question: 'Was ist der Unterschied zwischen KI‑Tool und KI‑Assistent:in?',
          answer: 'Ein KI‑Tool ist meist eine einzelne App oder Funktion, die du irgendwo anklickst. Ein KI‑Assistent hat eine konkrete Aufgabe in deinem Alltag, kennt eure Abläufe und arbeitet in euren vorhandenen Systemen mit – z.B. als Angebots‑Assistent im Posteingang oder als Support‑Assistent im Helpdesk. So wird aus „noch ein Tool" ein fester Platz in deinem Team.'
        },
        {
          question: 'Ersetzt KI meine Mitarbeiter:innen?',
          answer: 'Nein. Gut eingesetzte KI nimmt Routinearbeit weg, damit Menschen mehr Zeit für Entscheidungen, Beziehung, Kreativität und Verantwortung haben. klar:ki plant Assistent:innen bewusst so, dass der Mensch im Lead bleibt und KI zuarbeitet – nicht umgekehrt.'
        },
        {
          question: 'Brauche ich technisches Vorwissen, um KI‑Assistent:innen zu nutzen?',
          answer: 'Nein. Du musst nicht programmieren können – wichtig ist, dass du deine Prozesse kennst und sagen kannst, was dich heute Zeit und Nerven kostet. Die technische Umsetzung, Auswahl der Tools und Anbindung an eure Systeme übernimmt klar:ki gemeinsam mit dir.'
        }
      ]
    },
    {
      title: 'Nutzen & typische Einsatzbereiche',
      items: [
        {
          question: 'Wobei kann mir ein KI‑Assistent konkret helfen?',
          answer: 'Typische Einsatzfelder im Mittelstand sind z.B. Kundenservice (Standardanfragen beantworten), Rechnungs‑ und Angebotsprozesse, Wissensmanagement, interne IT‑Unterstützung oder Marketing‑Aufgaben wie E‑Mails und Texte vorbereiten. Wichtig: Wir schauen zuerst, wo heute wirklich Aufwand entsteht – und bauen dann Assistent:innen genau für diese Stellen.'
        },
        {
          question: 'Wie schnell macht sich ein KI‑Assistent bezahlt?',
          answer: 'Das hängt von Aufgabe und Startpunkt ab, aber oft werden pro Assistent:in mehrere Stunden pro Woche frei – z.B. durch automatisierte Erstantworten im Kundenservice oder das automatische Vorbereiten von Dokumenten. In den Projekten steht immer die Frage im Mittelpunkt: „Wieviel wiederkehrende Arbeit können wir hier sinnvoll reduzieren, ohne Qualität zu verlieren?"'
        },
        {
          question: 'Ist KI für mein (relativ kleines) Unternehmen überhaupt relevant?',
          answer: 'Gerade kleinere und mittlere Unternehmen profitieren stark, weil sie keine ganzen Abteilungen aufbauen müssen, um professionell zu arbeiten. Ein gut geplanter KI‑Assistent kann sich anfühlen wie eine zusätzliche Fachkraft, ohne Recruiting, Einschulung oder Krankenstände.'
        }
      ]
    },
    {
      title: 'Sicherheit, Daten & Verantwortung',
      items: [
        {
          question: 'Ist das nicht gefährlich – was passiert mit unseren Daten?',
          answer: 'Seriöse KI‑Lösungen bieten heute klare Einstellungen, wo Daten gespeichert werden, wer Zugriff hat und wie lange Informationen aufbewahrt werden. In klar:ki‑Projekten wählen wir die Umgebung so, dass sie zu euren Anforderungen an Datenschutz und Vertraulichkeit passt – und definieren, welche Daten ein Assistent sehen darf und welche nicht.'
        },
        {
          question: 'Kann ich mich auf die Ergebnisse eines KI‑Assistenten verlassen?',
          answer: 'KI kann sich irren oder etwas „erfinden". Deshalb bauen wir Assistent:innen so, dass sie aus definierten Quellen arbeiten (z.B. eurem Handbuch, Vorlagen, FAQs) und bei Unsicherheit zurückfragen oder an Menschen übergeben. Wo es kritisch wird (Recht, Finanzen, Personal), bleibt immer eine menschliche Freigabe als letzter Schritt.'
        },
        {
          question: 'Wie stelle ich sicher, dass meine Mitarbeitenden mitziehen – und keine Angst bekommen?',
          answer: 'Wir holen Mitarbeitende früh ins Boot, zeigen konkrete Entlastung und binden sie in die Gestaltung der Assistent:innen ein. Wenn Teams sehen, dass sie weniger Zeit mit Copy‑Paste und Suchen verbringen und mehr Zeit für Kund:innen oder Projekte haben, steigt die Akzeptanz spürbar.'
        }
      ]
    },
    {
      title: 'Start & nächste Schritte',
      items: [
        {
          question: 'Ich habe „nur eine vage Ahnung" von KI – bin ich bei klar:ki richtig?',
          answer: 'Ja. klar:ki ist genau für Menschen gemacht, die merken „Ich sollte mich damit beschäftigen", aber kein eigenes KI‑Team haben und keine Lust auf Buzzwords. Wir übersetzen technische Möglichkeiten in verständliche Rollenbilder und konkrete nächste Schritte in deinem Alltag.'
        },
        {
          question: 'Was brauche ich, bevor wir starten?',
          answer: 'Hilfreich sind: Zugang zu deinen wichtigsten Tools (z.B. E‑Mail, CRM, Projektmanagement), eine grobe Idee, wo heute viel Zeit verloren geht, und die Bereitschaft, gemeinsam zu schauen, welche Prozesse sich für KI‑Unterstützung eignen. Den Rest klären wir im Klarheitsgespräch.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header onBookingClick={openBookingModal} />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-32 px-6 lg:px-12 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-[#0A6AF0] font-normal mb-4 uppercase tracking-wide">
            Der klar:ki‑Ansatz – von der Ahnungslosigkeit zur Klarheit
          </p>
          <h1 className="text-4xl lg:text-6xl text-[#042A60] mb-6 font-light">
            Häufige Fragen zu KI und KI‑Assistent:innen – einfach erklärt
          </h1>
          <p className="text-base text-[#042A60]/80 leading-relaxed">
            Viele haben schon „irgendwas mit KI" ausprobiert – aber selten mit einem klaren Plan. Auf dieser Seite findest du Antworten auf die wichtigsten Fragen: Was KI‑Assistent:innen wirklich sind, was sie können (und was nicht) und wie klar:ki dich Schritt für Schritt zu mehr Klarheit und echten Ergebnissen begleitet.
          </p>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-20 lg:py-32 px-6 lg:px-12 bg-[#D7D7D7]">
        <div className="max-w-4xl mx-auto space-y-16">
          {faqSections.map((section, sectionIndex) => (
            <div key={sectionIndex}>
              <h2 className="text-2xl lg:text-3xl text-[#042A60] mb-8 font-light">
                {section.title}
              </h2>
              <div className="space-y-4">
                {section.items.map((item, itemIndex) => {
                  const key = `${sectionIndex}-${itemIndex}`;
                  const isOpen = openItems[key];
                  
                  return (
                    <div 
                      key={itemIndex}
                      className="bg-white overflow-hidden"
                    >
                      <button
                        onClick={() => toggleItem(sectionIndex, itemIndex)}
                        className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-[#042A60]/5 transition-colors cursor-pointer"
                      >
                        <span className="text-base font-medium text-[#042A60] pr-4">
                          {item.question}
                        </span>
                        <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-xl text-[#0A6AF0] flex-shrink-0 w-6 h-6 flex items-center justify-center`}></i>
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-5">
                          <p className="text-sm text-[#042A60]/80 leading-relaxed">
                            {item.answer}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 px-6 lg:px-12 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl lg:text-5xl text-[#042A60] mb-6 font-light">
            Noch Fragen offen?
          </h2>
          <p className="text-base text-[#042A60]/80 leading-relaxed mb-8">
            Im Klarheitsgespräch schauen wir gemeinsam, wo KI‑Assistent:innen in deinem Unternehmen den größten Unterschied machen können – ohne Buzzwords, dafür mit konkreten nächsten Schritten.
          </p>
          <button
            onClick={openBookingModal}
            className="px-8 py-3 bg-[#0A6AF0] text-white text-base font-normal hover:bg-[#042A60] transition-colors cursor-pointer whitespace-nowrap inline-flex items-center justify-center"
          >
            Klarheitsgespräch buchen
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>
      </section>

      <Footer />
      <BookingModal isOpen={isBookingModalOpen} onClose={closeBookingModal} />
    </div>
  );
}
